import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { PersonalData, AddressData, InsuranceData, GuardianData, ConsentType, ItalyConsents } from '../types'
import { getTenantSlug, getTenantConfig } from '../context/TenantContext'
import { setPatientSessionToken } from '../services/api'

/** A document that went to the practice: storage key + storage file name (the webhook's uploadedDocuments) */
export interface UploadedDocument {
  id: string
  filename: string
  documentType?: string
}

/** A file from the upload step, already stored on the server */
export interface StepUpload {
  /** QuestionnaireUpload id */
  id: string
  storageKey: string
  filename: string
  /** the name the patient's device gave it */
  name: string
  size: number
  documentType: string
  at: string
  /** sent with "Weiter" (part of a document in uploadedDocuments) */
  sent: boolean
}

/** A PTV 11 / Absageprotokoll file of the psychotherapy details, stored on the server */
export interface PtUpload {
  id: string
  kind: 'ptv11' | 'absageprotokoll'
  name: string
  size: number
}

export type Cohort = 'standard' | 'psychotherapy' | 'psychotherapy_returning'

/** What the server pinned for a returning psychotherapy patient (only the new parts are asked) */
export interface PtReturning {
  privacySigned: boolean
  billingSigned: boolean
  hasMandate: boolean
}

interface OnboardingState {
  // Session (magic-link patient session JWT)
  sessionToken: string | null
  patientId: string | null

  // Wizard position — persisted so reloads/redirects resume at the same step
  currentStep: number

  // Step 1: Patient Number
  patientNumber: string
  patientNumberLocked: boolean

  // Step 2: Phone
  phoneNumber: string
  agreedToTerms: boolean

  // Yousign
  yousignRequestId: string | null
  yousignSignerId: string | null
  yousignDocuments: { id: string; nature: string }[]
  yousignDataHash: string | null
  // The phone number the SR was created with — the OTP SMS goes HERE, not to
  // whatever the phone field currently shows (see ensureSignatureRequest)
  yousignPhoneNumber: string | null

  // Step 4: Personal Data
  personalData: PersonalData

  // Step 5: Address Data
  addressData: AddressData

  // Address verification result (OSM/Photon, best-effort)
  addressCheck: { status: string; normalized?: string; lat?: number; lon?: number } | null

  // Step 4: Insurance Data
  insuranceData: InsuranceData

  // Guardian Data (asked in Step 4)
  guardianData: GuardianData

  // Step 6: Document Upload — what went to the practice (one entry per document; for the webhook)
  uploadedDocuments: UploadedDocument[]
  // The files the patient uploaded in that step: stored at once, sent on "Weiter" (one PDF per type)
  stepUploads: StepUpload[]

  // Steps 8-10: Consents
  consents: Record<ConsentType, boolean>

  // Italy intake consents (IT org only)
  italyConsents: ItalyConsents

  // Step 11: SEPA
  sepaOptOut: boolean

  // Cohort — pinned when the magic link was exchanged, never re-resolved mid-flow (src/flow/flow.ts)
  cohort: Cohort
  /** Opened with an admin test link: test mode, nothing binding */
  cohortTest: boolean
  /** Psychotherapy variant: the contract steps in signing order (server-decided; sessions from before 2026-10-29) */
  ptContractKeys: string[]
  /** …per insurance type (public / private / none) — the form picks by the patient's answer */
  ptContractKeysByInsurance: Record<string, string[]> | null
  ptReturning: PtReturning | null
  /** Psychotherapy details: answer codes (server/src/services/vertraegeAnswers.ts) */
  ptAnswers: Record<string, unknown>
  ptUploads: PtUpload[]
  /** Read-and-agreed ticks of the contracts that are not one of the three onboarding consents */
  ptConfirmations: Record<string, boolean>
  ptStopped: boolean
  ptCompleted: boolean
  /** Contract key → Youtrust document id, for requests that mix template and uploaded documents */
  yousignDocumentMap: Record<string, string> | null

  // Actions
  setSessionToken: (token: string | null) => void
  setPatientId: (id: string) => void
  setCurrentStep: (step: number) => void
  cardSaved: boolean
  sepaDropinCompleted: boolean
  setCardSaved: (saved: boolean) => void
  setSepaDropinCompleted: (v: boolean) => void
  cardOptOut: boolean
  setCardOptOut: (v: boolean) => void
  setPatientNumber: (num: string) => void
  setPatientNumberLocked: (locked: boolean) => void
  setPhoneNumber: (phone: string) => void
  setAgreedToTerms: (agreed: boolean) => void
  setPersonalData: (data: Partial<PersonalData>) => void
  setAddressData: (data: Partial<AddressData>) => void
  setItalyConsents: (data: Partial<ItalyConsents>) => void
  setAddressCheck: (check: { status: string; normalized?: string; lat?: number; lon?: number } | null) => void
  setInsuranceData: (data: Partial<InsuranceData>) => void
  setGuardianData: (data: Partial<GuardianData>) => void
  addDocument: (doc: UploadedDocument) => void
  removeDocument: (id: string) => void
  setUploadedDocuments: (docs: UploadedDocument[]) => void
  setStepUploads: (next: StepUpload[] | ((prev: StepUpload[]) => StepUpload[])) => void
  setConsent: (type: ConsentType, agreed: boolean) => void
  setSepaOptOut: (optOut: boolean) => void
  setYousignData: (requestId: string, signerId: string, phoneNumber: string) => void
  setYousignDocuments: (documents: { id: string; nature: string }[]) => void
  setYousignDataHash: (hash: string | null) => void
  setYousignDocumentMap: (map: Record<string, string> | null) => void
  setCohort: (c: { cohort: Cohort; test: boolean; contractKeys?: string[]; contractKeysByInsurance?: Record<string, string[]> | null; returning?: PtReturning | null }) => void
  setPtAnswers: (patch: Record<string, unknown>) => void
  setPtUploads: (next: PtUpload[] | ((prev: PtUpload[]) => PtUpload[])) => void
  setPtConfirmation: (key: string, agreed: boolean) => void
  setPtStopped: (v: boolean) => void
  setPtCompleted: (v: boolean) => void
  reset: () => void
}

const initialPersonalData: PersonalData = {
  gender: '',
  firstName: '',
  lastName: '',
  email: '',
  birthDate: '',
  birthPlace: '',
  codiceFiscale: '',
  codiceFiscaleAbsent: false,
  generalPractitionerName: '',
  generalPractitionerPhone: '',
  generalPractitionerEmail: '',
  idDocumentType: '',
  idDocumentNumber: '',
  idDocumentIssuedBy: '',
  idDocumentIssuedOn: '',
}

const initialAddressData: AddressData = {
  address: '',
  street: '',
  houseNumber: '',
  noHouseNumber: false,
  addressLine2: '',
  city: '',
  state: '',
  province: '',
  postalCode: '',
  country: getTenantConfig()?.defaultCountry || 'Germany',
  domicileSameAsResidence: true,
  domicileAddress: '',
  domicileCity: '',
  domicileState: '',
  domicilePostalCode: '',
  domicileCountry: '',
}

const initialItalyConsents: ItalyConsents = {
  healthDataAccess: false,
  marketing: false,
  postVisitSurvey: false,
  unsignedReportEmail: false,
  televisitRecording: false,
  termsPrivacy: false,
}

const initialInsuranceData: InsuranceData = {
  insuranceType: '',
  insuranceCountry: getTenantConfig()?.defaultCountry || 'Germany',
  insuranceProvider: '',
  insuranceProviderOther: '',
}

const initialGuardianData: GuardianData = {
  hasGuardian: false,
  guardianName: '',
  guardianAddress: '',
  guardianScope: '',
  guardianDocumentFilename: '',
  guardianType: '',
  guardianFirstName: '',
  guardianLastName: '',
  guardianBirthDate: '',
  guardianBirthPlace: '',
  guardianTaxCode: '',
  guardianPhone: '',
  guardianEmail: '',
  guardianStreet: '',
  guardianHouseNumber: '',
  guardianPostalCode: '',
  guardianCity: '',
  guardianSignatures: '',
  guardian2FirstName: '',
  guardian2LastName: '',
  guardian2Phone: '',
  guardian2Email: '',
  guardianSingleSignatureReason: '',
}

const initialConsents: Record<ConsentType, boolean> = {
  treatment_contract: false,
  privacy_policy: false,
  billing_consent: false,
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      // Initial state
      sessionToken: null,
      patientId: null,
      currentStep: 1,
      patientNumber: '',
      patientNumberLocked: false,
      phoneNumber: '',
      agreedToTerms: false,
      yousignRequestId: null,
      yousignSignerId: null,
      yousignDocuments: [],
      yousignDataHash: null,
      yousignPhoneNumber: null,
      personalData: initialPersonalData,
      addressData: initialAddressData,
      addressCheck: null,
      insuranceData: initialInsuranceData,
      guardianData: initialGuardianData,
      uploadedDocuments: [],
      stepUploads: [],
      consents: initialConsents,
      italyConsents: initialItalyConsents,
      sepaOptOut: false,
      cardSaved: false,
      sepaDropinCompleted: false,
      cardOptOut: false,
      cohort: 'standard',
      cohortTest: false,
      ptContractKeys: [],
      ptContractKeysByInsurance: null,
      ptReturning: null,
      ptAnswers: {},
      ptUploads: [],
      ptConfirmations: {},
      ptStopped: false,
      ptCompleted: false,
      yousignDocumentMap: null,

      // Actions
      setSessionToken: (token) => {
        setPatientSessionToken(token)
        set({ sessionToken: token })
      },
      setPatientId: (id) => set({ patientId: id }),
      setCurrentStep: (step) => set({ currentStep: step }),

      setCardSaved: (saved) => set({ cardSaved: saved }),
      setSepaDropinCompleted: (v) => set({ sepaDropinCompleted: v }),

      setCardOptOut: (v) => set({ cardOptOut: v }),
      setPatientNumber: (num) => set({ patientNumber: num }),
      setPatientNumberLocked: (locked) => set({ patientNumberLocked: locked }),
      setPhoneNumber: (phone) => set({ phoneNumber: phone }),
      setAgreedToTerms: (agreed) => set({ agreedToTerms: agreed }),

      setPersonalData: (data) =>
        set((state) => ({
          personalData: { ...state.personalData, ...data },
        })),

      setItalyConsents: (data) =>
        set((state) => ({
          italyConsents: { ...state.italyConsents, ...data },
        })),

      setAddressData: (data) =>
        set((state) => ({
          addressData: { ...state.addressData, ...data },
          // any manual edit invalidates a previous verification result
          addressCheck: null,
        })),

      setAddressCheck: (check) => set({ addressCheck: check }),

      setInsuranceData: (data) =>
        set((state) => ({
          insuranceData: { ...state.insuranceData, ...data },
        })),

      setGuardianData: (data) =>
        set((state) => ({
          guardianData: { ...state.guardianData, ...data },
        })),

      addDocument: (doc) =>
        set((state) => ({
          uploadedDocuments: [...state.uploadedDocuments, doc],
        })),

      removeDocument: (id) =>
        set((state) => ({
          uploadedDocuments: state.uploadedDocuments.filter((d) => d.id !== id),
        })),

      setUploadedDocuments: (docs) =>
        set({ uploadedDocuments: docs }),

      setStepUploads: (next) =>
        set((state) => ({ stepUploads: typeof next === 'function' ? next(state.stepUploads) : next })),

      setConsent: (type, agreed) =>
        set((state) => ({
          consents: { ...state.consents, [type]: agreed },
        })),

      setSepaOptOut: (optOut) => set({ sepaOptOut: optOut }),

      setYousignData: (requestId, signerId, phoneNumber) =>
        set({ yousignRequestId: requestId, yousignSignerId: signerId, yousignPhoneNumber: phoneNumber }),

      setYousignDocuments: (documents) =>
        set({ yousignDocuments: documents }),

      setYousignDataHash: (hash) =>
        set({ yousignDataHash: hash }),

      setYousignDocumentMap: (map) => set({ yousignDocumentMap: map }),

      setCohort: ({ cohort, test, contractKeys, contractKeysByInsurance, returning }) =>
        set({ cohort, cohortTest: test, ptContractKeys: contractKeys ?? [], ptContractKeysByInsurance: contractKeysByInsurance ?? null, ptReturning: returning ?? null }),

      setPtAnswers: (patch) =>
        set((state) => {
          const next = { ...state.ptAnswers }
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === null || v === '') delete next[k]
            else next[k] = v
          }
          return { ptAnswers: next }
        }),

      setPtUploads: (next) =>
        set((state) => ({ ptUploads: typeof next === 'function' ? next(state.ptUploads) : next })),

      setPtConfirmation: (key, agreed) =>
        set((state) => ({ ptConfirmations: { ...state.ptConfirmations, [key]: agreed } })),

      setPtStopped: (v) => set({ ptStopped: v }),
      setPtCompleted: (v) => set({ ptCompleted: v }),

      reset: () => {
        setPatientSessionToken(null)
        set({
          sessionToken: null,
          patientId: null,
          currentStep: 1,
          patientNumber: '',
          patientNumberLocked: false,
          phoneNumber: '',
          agreedToTerms: false,
          yousignRequestId: null,
          yousignSignerId: null,
          yousignDocuments: [],
          yousignDataHash: null,
          yousignPhoneNumber: null,
          personalData: initialPersonalData,
          addressData: initialAddressData,
          addressCheck: null,
          insuranceData: initialInsuranceData,
          guardianData: initialGuardianData,
          uploadedDocuments: [],
          stepUploads: [],
          consents: initialConsents,
          italyConsents: initialItalyConsents,
          sepaOptOut: false,
          cardSaved: false,
          cardOptOut: false,
          cohort: 'standard',
          cohortTest: false,
          ptContractKeys: [],
          ptContractKeysByInsurance: null,
          ptReturning: null,
          ptAnswers: {},
          ptUploads: [],
          ptConfirmations: {},
          ptStopped: false,
          ptCompleted: false,
          yousignDocumentMap: null,
        })
      },
    }),
    {
      name: `gam-onboarding-${getTenantSlug() || 'default'}`,
      partialize: (state) => ({
        sessionToken: state.sessionToken,
        patientId: state.patientId,
        currentStep: state.currentStep,
        cardSaved: state.cardSaved,
        sepaDropinCompleted: state.sepaDropinCompleted,
        cardOptOut: state.cardOptOut,
        patientNumber: state.patientNumber,
        patientNumberLocked: state.patientNumberLocked,
        phoneNumber: state.phoneNumber,
        // Yousign IDs persisted so they survive page reloads between steps on mobile
        yousignRequestId: state.yousignRequestId,
        yousignSignerId: state.yousignSignerId,
        yousignDocuments: state.yousignDocuments,
        yousignDataHash: state.yousignDataHash,
        yousignPhoneNumber: state.yousignPhoneNumber,
        personalData: state.personalData,
        addressData: state.addressData,
        addressCheck: state.addressCheck,
        insuranceData: state.insuranceData,
        guardianData: state.guardianData,
        uploadedDocuments: state.uploadedDocuments, // Persist uploaded doc references for webhook
        stepUploads: state.stepUploads, // stored but maybe not sent yet — a reload must not lose them
        cohort: state.cohort,
        cohortTest: state.cohortTest,
        ptContractKeys: state.ptContractKeys,
        ptContractKeysByInsurance: state.ptContractKeysByInsurance,
        ptReturning: state.ptReturning,
        ptAnswers: state.ptAnswers,
        ptUploads: state.ptUploads,
        ptConfirmations: state.ptConfirmations,
        ptStopped: state.ptStopped,
        ptCompleted: state.ptCompleted,
        yousignDocumentMap: state.yousignDocumentMap,
      }),
      onRehydrateStorage: () => (state) => {
        // Re-attach the persisted patient session token to the API client
        if (state?.sessionToken) setPatientSessionToken(state.sessionToken)
      },
    }
  )
)
